import React, { useState, useEffect } from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setBreadCrumbs,
  setCurrent,
  setNavigationComponents,
} from "features/navigation/navigationSlice";
import Card from "ui/Card";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "ui/buttons/PrimaryButton";
import DataGrid from "components/data-grid/DataGrid";

export default function SalesIndex(isDashboard) {
  let navigate = useNavigate();
  let { report } = useParams();
  let dispatch = useDispatch();
  let location = useLocation();

  const isListView =
    location.pathname === "/sales" || location.pathname === "/sales/";

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState([]);
  const [showFilter, setShowFilter] = useState(false);

  let userData = useSelector((state) => state.data.userData);
  let outlets = userData?.outlets;
  let [params, setParams] = React.useState({
    start: "2023-01-01",
    end: "2024-12-31",
    outlet: null,
  });

  const statusOptions = [
    { value: "void", label: "Void" },
    { value: "completed", label: "Completed" },
  ];

  const toggleFilter = () => {
    if (!showFilter) {
      setTempSelectedStatuses(selectedStatuses);
    }
    setShowFilter(!showFilter);
  };

  const handleCheckboxChange = (value) => {
    setTempSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const applyFilters = () => {
    setSelectedStatuses(tempSelectedStatuses);
    setShowFilter(false);
  };

  const cancelFilter = () => {
    setShowFilter(false);
  };

  React.useEffect(() => {
    dispatch(
      setCurrent({
        name: `Sales`,
      })
    );

    if (isListView) {
      dispatch(
        setNavigationComponents(
          <div className="flex flex-row space-x-2">
            <PrimaryButton
              title={"New Sale"}
              onClick={() => {
                navigate("/sales/add");
              }}
            ></PrimaryButton>
          </div>
        )
      );
      dispatch(
        setBreadCrumbs([
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Sales",
            path: "/sales",
          },
          {
            name: "Sales",
            path: `/sales`,
          },
        ])
      );
    }
  }, [dispatch, navigate, isListView]);

  React.useEffect(() => {
    setParams({
      ...params,
      outlet: outlets ? outlets[0] : null,
    });
  }, []);

  const filterParams =
    selectedStatuses.length > 0
      ? (() => {
        if (selectedStatuses.length === 2) return undefined;
        if (selectedStatuses.includes("void") && !selectedStatuses.includes("completed")) {
          return { isVoid: "void" };
        }
        if (selectedStatuses.includes("completed") && !selectedStatuses.includes("void")) {
          return { isVoid: "completed" };
        }
        return undefined;
      })()
      : undefined;

  const externalParams = { viewAll: true, ...filterParams };

  return (
    <div className="relative">
      <div className=" grid grid-cols-1 gap-2  h-[calc(100vh-120px)] bg-slate-50  rounded-xl top-0 w-full">
        {isListView && (
          <Card heading={"All Sales"} description={"Find all the sales here"}>
            <div className="relative w-full">
              <div className="relative">
                <div className="absolute top-0 right-0 z-10">
                  <div className="relative">
                    <button
                      onClick={toggleFilter}
                      className="w-auto h-8 px-4 py-2 border rounded flex items-center space-x-2 text-sm bg-[#dce3ec] hover:bg-[#cbd5e1] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
                    >
                      <span className="hidden sm:inline">Filter</span>
                      <span className="sm:hidden">
                        <i className="fas fa-filter"></i>
                      </span>
                      <i className="fas fa-filter hidden sm:inline"></i>
                      {selectedStatuses.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {selectedStatuses.length}
                        </span>
                      )}
                    </button>

                    {showFilter && (
                      <div className="absolute right-0 mt-2 w-screen sm:w-64 sm:max-w-xs -left-4 sm:left-auto bg-white border rounded shadow-lg z-20 p-4">
                        <p className="font-medium mb-2 text-gray-700 text-sm md:text-base">
                          Filter by Status
                        </p>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {statusOptions.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={tempSelectedStatuses.includes(option.value)}
                                onChange={() => handleCheckboxChange(option.value)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4 pt-2 border-t border-gray-100">
                          <button
                            onClick={cancelFilter}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors order-2 sm:order-1"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={applyFilters}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors order-1 sm:order-2"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="py-3">
                  <DataGrid
                    searchable={true}
                    showHeader={true}
                    api={"/admin/report/sales"}
                    externalParams={externalParams}
                    paginationMode="server"
                    defaultPageSize={10}
                    pageSizeOptions={[10, 50, 100, 200, 500]}
                    cellFunction={(cell) => {
                      if (cell.column.id === "referenceNumber" || cell.column.accessor === "referenceNumber") {
                        return (
                          <p
                            onClick={() => navigate(`/sales/view/${cell.row.original.id}`)}
                            className="capitalize hover cursor-pointer"
                          >
                            {cell.value}
                          </p>
                        );
                      }
                      if (cell.column.id === "createdAt" || cell.column.accessor === "createdAt") {
                        return cell.value ? new Date(cell.value).toLocaleDateString() : "";
                      }
                      if (cell.column.id === "isVoid" || cell.column.accessor === "isVoid") {
                        return cell.value ? (
                          <p className="text-red-500">Void</p>
                        ) : (
                          <p className="text-green-500">Completed</p>
                        );
                      }
                      return <p className="capitalize">{cell.value ?? ""}</p>;
                    }}
                    height={"h-[calc(100vh-250px)]"}
                  />
                </div>
              </div>
            </div>
          </Card>
        )}
        <Outlet />
      </div>
    </div>
  );
}